/**
 * Proposals frontend helpers — Phase G4A CP2.
 *
 * Shared status metadata + step metadata + completion heuristics.
 * Consumed by Proposals.jsx (library), ProposalWizard.jsx (10-step
 * shell), ProposalCard.jsx (list rows), and the wizardSteps.jsx
 * per-step form components.
 *
 * Voice discipline: every user-facing string here is factual and
 * neutral — no banned phrases from either the G2A set or the
 * G4-specific additions ("guaranteed to win", "will get funded",
 * "will receive", "definitely qualify", "sure to be awarded").
 */

import {
  FileText,
  Wallet,
  AlertCircle,
  Lightbulb,
  Users,
  ClipboardList,
  Calendar,
  BookOpen,
  Sparkles,
  Gauge,
} from "lucide-react";

// ----------------------------------------------------------------------
// Status pill metadata — mirrors ProposalStatus enum in backend model.
// ----------------------------------------------------------------------

export const STATUS_META = {
  draft: {
    label: "Draft",
    tint: "rgb(148,163,184)", // slate — in-progress neutral
    tintBg: "rgba(148,163,184,0.12)",
    tintBorder: "rgba(148,163,184,0.42)",
  },
  generated: {
    label: "Generated",
    tint: "rgb(56,189,248)", // sky — AI-touched
    tintBg: "rgba(56,189,248,0.12)",
    tintBorder: "rgba(56,189,248,0.42)",
  },
  reviewed: {
    label: "Reviewed",
    tint: "rgb(52,211,153)", // emerald — user acknowledged
    tintBg: "rgba(52,211,153,0.12)",
    tintBorder: "rgba(52,211,153,0.42)",
  },
  exported: {
    label: "Exported",
    tint: "rgb(167,139,250)", // v2 accent — completed
    tintBg: "rgba(167,139,250,0.14)",
    tintBorder: "rgba(167,139,250,0.42)",
  },
  archived: {
    label: "Archived",
    tint: "rgb(100,116,139)", // deeper slate — soft-deleted terminal
    tintBg: "rgba(100,116,139,0.10)",
    tintBorder: "rgba(100,116,139,0.35)",
  },
};

export function statusMeta(s) {
  return STATUS_META[s] || STATUS_META.draft;
}

// ----------------------------------------------------------------------
// 10-step wizard definition.
//
// `key` values for steps 1-8 map 1:1 to the backend's `WIZARD_STEP_KEYS`
// set (models/proposal.py). Steps 9 and 10 have `key: null` — they
// don't persist to wizard_data (they'll populate `sections` and
// `quality_score` respectively in G4B/G4C).
// ----------------------------------------------------------------------

export const STEPS = [
  {
    number: 1,
    key: "business_overview",
    title: "Business Overview",
    kicker: "Who you are",
  },
  {
    number: 2,
    key: "funding_request",
    title: "Funding Request",
    kicker: "What you're asking for",
  },
  {
    number: 3,
    key: "problem_statement",
    title: "Problem Statement",
    kicker: "The problem you're addressing",
  },
  {
    number: 4,
    key: "solution",
    title: "Solution",
    kicker: "Your approach",
  },
  {
    number: 5,
    key: "community_impact",
    title: "Community Impact",
    kicker: "Who benefits",
  },
  {
    number: 6,
    key: "budget",
    title: "Budget",
    kicker: "Where the funds go",
  },
  {
    number: 7,
    key: "timeline",
    title: "Timeline",
    kicker: "When it happens",
  },
  {
    number: 8,
    key: "review",
    title: "Review",
    kicker: "Check your work",
  },
  {
    number: 9,
    key: null,
    title: "Generate Proposal",
    kicker: "AI-assisted drafting",
    locked: true,
  },
  {
    number: 10,
    key: null,
    title: "Quality Score",
    kicker: "Section review",
    locked: true,
  },
];

// Lucide icon per step number — kept out of the STEPS array so the
// object literal above stays readable.
export const STEP_ICONS = {
  1: FileText,
  2: Wallet,
  3: AlertCircle,
  4: Lightbulb,
  5: Users,
  6: ClipboardList,
  7: Calendar,
  8: BookOpen,
  9: Sparkles,
  10: Gauge,
};

// Approved honest-gate copy for step 10 — verified against
// the G2A + G4 banned-phrase sets during the CP1 audit. Do NOT
// swap for "coming soon" / "beta" / any hype phrase.
// (Step 9 is now active in G4B CP2 — the "AI generation lands
// in the next Grant Writer update" copy remains only for step 10.)
export const HONEST_GATE_COPY =
  "AI generation lands in the next Grant Writer update.";


// ----------------------------------------------------------------------
// G4B — 11 proposal output sections (populated by the AI Grant
// Writer). Keys mirror the backend's `SECTION_KEYS` list exactly;
// keep them in sync.
// ----------------------------------------------------------------------

export const SECTION_KEYS = [
  "executive_summary",
  "statement_of_need",
  "organization_background",
  "project_description",
  "goals_and_objectives",
  "budget_narrative",
  "timeline",
  "evaluation_plan",
  "sustainability_plan",
  "community_impact",
  "conclusion",
];

export const SECTION_DISPLAY_TITLES = {
  executive_summary: "Executive Summary",
  statement_of_need: "Statement of Need",
  organization_background: "Organization Background",
  project_description: "Project Description",
  goals_and_objectives: "Goals & Objectives",
  budget_narrative: "Budget Narrative",
  timeline: "Timeline",
  evaluation_plan: "Evaluation Plan",
  sustainability_plan: "Sustainability Plan",
  community_impact: "Community Impact",
  conclusion: "Conclusion",
};


// ----------------------------------------------------------------------
// Confidence tier — voice-clean labels per user CP2 brief.
// Explicit vocabulary: "Moderate" / "High" / "Very high".
// Do NOT introduce "Excellent" / "Perfect" / "Winning".
// ----------------------------------------------------------------------

export function confidenceTier(confidence) {
  if (confidence == null) {
    return {
      label: "Not scored",
      tint: "rgb(148,163,184)",
      tintBg: "rgba(148,163,184,0.10)",
      tintBorder: "rgba(148,163,184,0.35)",
    };
  }
  if (confidence >= 0.90) {
    return {
      label: "Very high",
      tint: "rgb(52,211,153)",
      tintBg: "rgba(52,211,153,0.12)",
      tintBorder: "rgba(52,211,153,0.42)",
    };
  }
  if (confidence >= 0.75) {
    return {
      label: "High",
      tint: "rgb(74,222,128)",
      tintBg: "rgba(74,222,128,0.12)",
      tintBorder: "rgba(74,222,128,0.42)",
    };
  }
  // 0.60-0.74 — Moderate. Below 0.60 is fail_closed and does not
  // surface a confidence chip (handled separately).
  return {
    label: "Moderate",
    tint: "rgb(251,191,36)",
    tintBg: "rgba(251,191,36,0.12)",
    tintBorder: "rgba(251,191,36,0.42)",
  };
}


// ----------------------------------------------------------------------
// Fail-reason chip labels — factual, non-alarmist. Rendered on
// fail-closed section cards. Server returns raw fail_reason codes;
// UI maps them here.
// ----------------------------------------------------------------------

export const FAIL_REASON_LABELS = {
  low_confidence: "Below confidence threshold",
  banned_phrase: "Language filter caught output",
  schema_fail: "Format did not match schema",
  api_error: "Service unavailable",
};

export function failReasonLabel(reason) {
  return FAIL_REASON_LABELS[reason] || "Draft not accepted";
}

// ----------------------------------------------------------------------
// Per-step completion heuristics for the sidebar.
// A step is "filled" when at least the primary field is populated.
// Non-primary fields are bonus context; users are never blocked from
// advancing (the wizard is opt-in-per-step, not a strict funnel).
// ----------------------------------------------------------------------

export function isStepFilled(stepKey, wizardData) {
  const d = (wizardData && wizardData[stepKey]) || {};
  switch (stepKey) {
    case "business_overview":
      return Boolean((d.name || "").trim());
    case "funding_request":
      return Number(d.amount_requested) > 0;
    case "problem_statement":
      return Boolean((d.problem_narrative || "").trim());
    case "solution":
      return Boolean((d.approach_narrative || "").trim());
    case "community_impact":
      return Boolean((d.impact_narrative || "").trim());
    case "budget":
      return Array.isArray(d.line_items) && d.line_items.length > 0;
    case "timeline":
      return Array.isArray(d.milestones) && d.milestones.length > 0;
    case "review":
      // Review step is filled when the user has typed any notes — a
      // scratch pad for last-pass thoughts before generation. Not
      // required to advance.
      return Boolean((d.user_notes || "").trim());
    default:
      return false;
  }
}

// ----------------------------------------------------------------------
// Document-Vault attach hints per step (per user brief §G3A.3 +
// CP2 dispatch — one picker per step, minimal UX).
//
// Each hint declares:
//   - `docType`: the canonical doc_type the picker queries
//   - `label`: the visible dropdown label
//   - `wizardField`: the wizard_data sub-key where the doc id is
//     stored (e.g. wizard_data.business_overview.ein_letter_doc_id)
// ----------------------------------------------------------------------

export const DOC_ATTACH_HINTS = {
  business_overview: {
    docType: "ein_letter",
    label: "Attach EIN letter",
    wizardField: "ein_letter_doc_id",
  },
  budget: {
    docType: "profit_and_loss",
    label: "Attach P&L statement",
    wizardField: "pnl_doc_id",
  },
  solution: {
    docType: "business_plan",
    label: "Attach business plan",
    wizardField: "business_plan_doc_id",
  },
  community_impact: {
    docType: "letter_of_support",
    label: "Attach letter of support",
    wizardField: "letter_of_support_doc_id",
  },
};

// ----------------------------------------------------------------------
// Timestamps — reuse the same short relative formatter as G3C-B so
// list surfaces read consistently across the app.
// ----------------------------------------------------------------------

export function formatRelative(iso, now = new Date()) {
  if (!iso) return "";
  const target = new Date(iso);
  const diffMs = target.getTime() - now.getTime();
  const absMs = Math.abs(diffMs);
  const past = diffMs < 0;

  const min = 60 * 1000;
  const hr = 60 * min;
  const day = 24 * hr;

  if (absMs < min) return past ? "just now" : "in a moment";
  if (absMs < hr) {
    const m = Math.round(absMs / min);
    return past ? `${m}m ago` : `in ${m}m`;
  }
  if (absMs < day) {
    const h = Math.round(absMs / hr);
    return past ? `${h}h ago` : `in ${h}h`;
  }
  const d = Math.round(absMs / day);
  if (past) {
    if (d === 1) return "yesterday";
    return `${d} days ago`;
  }
  if (d === 1) return "tomorrow";
  return `in ${d} days`;
}
